import React, { useState } from 'react';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiBox from '@material-ui/core/Box';
import MuiTooltip from '@material-ui/core/Tooltip';
import MuiButton from '@material-ui/core/Button';
import TableSelectColumn from '../../components/TableSelectColumn';
import ConfirmationDialog from '../../components/ConfirmationDialog';

const styles = {
  truncate: {
    whiteSpace: 'normal',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    lineHeight: '1.2',
  },
  match: {
    color: '#4caf50',
    fontWeight: 'bold',
  },
  mismatch: {
    color: '#f44336',
    fontWeight: 'bold',
  },
  pending: {
    color: '#ff9800',
    fontStyle: 'italic',
  },
};

function RowsCell({ pgRows, snowflakeRows }) {
  // Show PostgreSQL rows (from report_file table) and comparison with Snowflake
  if (pgRows === null || pgRows === undefined) {
    return (
      <MuiTooltip title="No PostgreSQL rows data. This should come from the report_file table.">
        <span style={styles.pending}>N/A</span>
      </MuiTooltip>
    );
  }

  const pgRowsNum = parseInt(pgRows, 10);
  const snowflakeRowsNum = snowflakeRows ? parseInt(snowflakeRows, 10) : null;

  let displayValue = pgRowsNum.toLocaleString();
  let style = { fontSize: '0.85em', color: '#000' }; // Always black
  let tooltipText = `PostgreSQL rows: ${pgRowsNum.toLocaleString()}`;

  if (snowflakeRowsNum !== null) {
    const difference = Math.abs(pgRowsNum - snowflakeRowsNum);
    const isMatch = difference === 0;

    if (isMatch) {
      tooltipText += `\nSnowflake rows: ${snowflakeRowsNum.toLocaleString()}\n✅ Match`;
    } else {
      displayValue += ` (${snowflakeRowsNum.toLocaleString()})`;
      tooltipText += `\nSnowflake rows: ${snowflakeRowsNum.toLocaleString()}\n❌ Difference: ${difference.toLocaleString()}`;
    }
  } else {
    // Only PostgreSQL data available
    tooltipText += '\nSnowflake rows: N/A';
  }

  return (
    <MuiTooltip title={tooltipText}>
      <span style={style}>{displayValue}</span>
    </MuiTooltip>
  );
}

function TotalValueUsdCell({ totalValue, currency, hasMixedCurrencies, currencies }) {
  // Handle mixed currencies case
  if (hasMixedCurrencies) {
    const currencyList = currencies ? currencies.join(', ') : 'Multiple currencies';
    return (
      <MuiTooltip
        title={`Mixed currencies in this file: ${currencyList}. Total value not shown as it would be misleading to sum different currencies.`}>
        <span style={{ color: '#ff9800', fontStyle: 'italic', fontSize: '0.85em' }}>Mixed 💱</span>
      </MuiTooltip>
    );
  }

  if (totalValue === null || totalValue === undefined) {
    // Debug: Show when we're not receiving any data
    return (
      <MuiTooltip title="No Snowflake data received. Check if server is running with latest changes and Snowflake is configured.">
        <span style={styles.pending}>N/A</span>
      </MuiTooltip>
    );
  }

  // Convert to USD if not already
  const formatValueInUsd = (value, curr) => {
    if (!value || value === '0.00') return '$0.00';

    const numValue = parseFloat(value);

    // For now, display the value as-is since we don't have conversion rates
    // In the future, we could add currency conversion here
    let displayValue;
    if (curr === 'USD') {
      displayValue = `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      // Show original currency with note
      const currencySymbol = curr === 'EUR' ? '€' : curr === 'GBP' ? '£' : `${curr} `;
      if (curr === 'EUR' || curr === 'GBP') {
        displayValue = `${currencySymbol}${numValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      } else {
        displayValue = `${numValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${currencySymbol}`;
      }
    }

    return displayValue;
  };

  const displayValue = formatValueInUsd(totalValue, currency);

  const tooltipText =
    currency === 'USD'
      ? `Total payout from Snowflake: ${displayValue}`
      : `Total payout from Snowflake: ${displayValue} (${currency} - conversion to USD not applied)`;

  return (
    <MuiTooltip title={tooltipText}>
      <span style={{ color: '#000', fontSize: '0.85em' }}>
        {displayValue}
        {currency !== 'USD' && (
          <span style={{ fontSize: '0.7em', color: '#666', marginLeft: '2px' }}>({currency})</span>
        )}
      </span>
    </MuiTooltip>
  );
}

function ActionButton({ setFileStatusToPending, updatingFile, filename, isSelected, onBulkAction, selectedCount }) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleButtonClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    // If this item is selected and there are multiple selected items, use bulk action
    if (isSelected && selectedCount > 1) {
      onBulkAction();
    } else {
      setFileStatusToPending();
    }
    setConfirmOpen(false);
  };

  if (updatingFile) return <span>...updating</span>;

  const isMultipleSelected = isSelected && selectedCount > 1;
  const title = isMultipleSelected ? `Set ${selectedCount} Files to Pending` : 'Set File to Pending';
  const content = isMultipleSelected
    ? `Are you sure you want to set ${selectedCount} selected files to pending status? This action cannot be undone.`
    : `Are you sure you want to set "${filename}" to pending status? This action cannot be undone.`;

  return (
    <>
      <MuiButton onClick={handleButtonClick}>Set To Pending</MuiButton>
      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        title={title}
        content={content}
        confirmText="Set to Pending"
        cancelText="Cancel"
        loading={updatingFile}
      />
    </>
  );
}

function ReportFilesTableRow({
  file_url,
  status,
  aws_modified_date,
  dsp_name,
  rows,
  snowflake_rows,
  snowflake_total_value,
  snowflake_currency,
  has_mixed_currencies,
  currencies,
  setFileStatusToPending,
  updatingFile,
  isSelected,
  onToggleSelect,
  onBulkAction,
  selectedCount,
}) {
  // Extract filename from URL for display
  const filename = file_url ? file_url.split('/').pop() : 'Unknown file';

  const handleCheckboxChange = () => {
    onToggleSelect();
  };

  return (
    <MuiTableRow selected={isSelected}>
      <TableSelectColumn checked={isSelected} onChange={handleCheckboxChange} tooltip="Select this file" />
      <MuiTableCell>{aws_modified_date}</MuiTableCell>
      <MuiTableCell>
        {dsp_name ? dsp_name : <span style={{ color: '#666', fontStyle: 'italic' }}>System</span>}
      </MuiTableCell>
      <MuiTableCell>
        <MuiTooltip title={file_url}>
          <MuiBox style={styles.truncate}>{file_url}</MuiBox>
        </MuiTooltip>
      </MuiTableCell>
      <MuiTableCell>{status}</MuiTableCell>
      {/* <MuiTableCell>
        <RowsCell pgRows={rows} snowflakeRows={snowflake_rows} />
      </MuiTableCell> */}
      {/* Rows column cell - commented for future analysis of column necessity */}
      {/* <MuiTableCell>
        <TotalValueUsdCell
          totalValue={snowflake_total_value}
          currency={snowflake_currency}
          hasMixedCurrencies={has_mixed_currencies}
          currencies={currencies}
        />
      </MuiTableCell> */}
      {/* Values column cell - commented for future analysis of column necessity */}
      <MuiTableCell>
        {status === 'error' ? (
          <ActionButton
            setFileStatusToPending={setFileStatusToPending}
            updatingFile={updatingFile}
            filename={filename}
            isSelected={isSelected}
            onBulkAction={onBulkAction}
            selectedCount={selectedCount}
          />
        ) : null}
      </MuiTableCell>
    </MuiTableRow>
  );
}

export default ReportFilesTableRow;
